import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/hooks/useAuth';
import { authService } from '../../services/authService';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { useLayout } from './LayoutContext';
import { PRIMARY_NAV, ADMIN_NAV, canSee } from './navConfig';
import { ICONS, ChevronIcon, MenuIcon, LogoutIcon } from './icons';
import './Sidebar.css';

export function Sidebar() {
  const { user, userRole } = useAuth();
  const { collapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useLayout();
  const location = useLocation();
  const navigate = useNavigate();

  const onAdminRoute = location.pathname.startsWith('/admin');
  const [adminOpen, setAdminOpen] = useState(onAdminRoute);

  const closeMobile = () => setMobileOpen(false);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const showAdmin = canSee(ADMIN_NAV, userRole);
  const AdminGlyph = ICONS[ADMIN_NAV.icon];
  const initials = (user?.email || '?').slice(0, 2).toUpperCase();

  return (
    <>
      <div
        className={`sidebar-scrim ${mobileOpen ? 'open' : ''}`}
        onClick={closeMobile}
        aria-hidden="true"
      />
      <nav
        className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
        aria-label="Main navigation"
      >
        <div className="sidebar-header">
          <NavLink to="/dashboard" className="sidebar-logo" onClick={closeMobile}>
            <Logo collapsed={collapsed} />
          </NavLink>
          <button
            type="button"
            className="sidebar-collapse"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            <MenuIcon />
          </button>
        </div>

        <div className="sidebar-nav">
          {PRIMARY_NAV.filter((item) => canSee(item, userRole)).map((item) => {
            const Icon = ICONS[item.icon];
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className="nav-item"
                onClick={closeMobile}
                title={collapsed ? item.label : undefined}
              >
                <span className="nav-icon"><Icon /></span>
                <span className="nav-label">{item.label}</span>
              </NavLink>
            );
          })}

          {showAdmin && (
            <div className="admin-group">
              <button
                type="button"
                className={`nav-item nav-parent ${onAdminRoute ? 'active-parent' : ''}`}
                onClick={() => setAdminOpen((o) => !o)}
                aria-expanded={adminOpen}
                aria-controls="admin-subnav"
                title={collapsed ? ADMIN_NAV.label : undefined}
              >
                <span className="nav-icon"><AdminGlyph /></span>
                <span className="nav-label">{ADMIN_NAV.label}</span>
                <span className={`nav-chevron ${adminOpen ? 'open' : ''}`}>
                  <ChevronIcon width={16} height={16} />
                </span>
              </button>

              <div id="admin-subnav" className={`admin-subnav ${adminOpen ? 'open' : ''}`}>
                {ADMIN_NAV.groups.map((g) => {
                  const items = g.items.filter((it) => canSee(it, userRole));
                  if (items.length === 0) return null;
                  return (
                    <div key={g.group} className="admin-subgroup">
                      <div className="admin-grouplabel">{g.group}</div>
                      {items.map((it) => (
                        <NavLink
                          key={it.path}
                          to={it.path}
                          className="nav-subitem"
                          onClick={closeMobile}
                        >
                          <span className="subitem-dot" aria-hidden="true" />
                          <span className="nav-label">{it.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="sidebar-footer">
          <ThemeToggle />
          <div className="sidebar-account">
            <span className="account-avatar" aria-hidden="true">{initials}</span>
            <span className="account-info">
              <span className="account-email">{user?.email}</span>
              <span className="account-role">{userRole?.role}</span>
            </span>
          </div>
          <button type="button" className="sidebar-logout" onClick={handleLogout}>
            <span className="nav-icon"><LogoutIcon /></span>
            <span className="nav-label">Logout</span>
          </button>
        </div>
      </nav>
    </>
  );
}
